
import { toast } from 'sonner';
import { compressToUTF16, decompressFromUTF16 } from 'lz-string';
import { Category, Resource, AppSettings } from '../types/resource';

export const STORAGE_VERSION = '3.2.0'; // Increment version to force fresh data load

export const LOCAL_STORAGE_KEYS = {
  CATEGORIES: 'categories',
  RESOURCES: 'resources',
  IS_ADMIN: 'isAdmin',
  ADMIN_CREDENTIALS: 'adminCredentials',
  APP_SETTINGS: 'appSettings',
  VERSION: 'storageVersion'
};

export const compressData = (data: string): string => {
  try {
    if (data.length < 1000) return data;
    return compressToUTF16(data);
  } catch (error) {
    console.error('Compression error:', error);
    return data;
  }
};

export const decompressData = (data: string): string => {
  try {
    if (data.startsWith('ᓄ') || data.includes('௡') || data.includes('缦')) {
      return decompressFromUTF16(data) || data;
    }
    return data;
  } catch (error) {
    console.error('Decompression error:', error);
    return data;
  }
};

export const loadData = <T>(key: string, defaultValue: T): T => {
  try {
    console.log(`Loading ${key} from localStorage`);
    const rawData = localStorage.getItem(key);
    
    if (!rawData) {
      console.log(`No ${key} found in localStorage, using defaults`);
      return defaultValue;
    }
    
    try {
      return JSON.parse(rawData);
    } catch (error) {
      console.log(`Failed to parse ${key} directly, might be compressed`);
      try {
        const decompressed = decompressFromUTF16(rawData);
        if (decompressed) {
          return JSON.parse(decompressed);
        }
      } catch (e) {
        console.error(`Failed to decompress ${key}:`, e);
      }
      console.error(`Using default data for ${key} due to parsing errors`);
      return defaultValue;
    }
  } catch (error) {
    console.error(`Error loading ${key}:`, error);
    return defaultValue;
  }
};

export const saveToLocalStorage = (key: string, data: any): boolean => {
  try {
    const jsonData = JSON.stringify(data);
    if (jsonData.length < 50000) {
      localStorage.setItem(key, jsonData);
      console.log(`${key} saved to localStorage directly:`, Array.isArray(data) ? data.length : 'object');
      return true;
    }
    try {
      const compressedData = compressToUTF16(jsonData);
      localStorage.setItem(key, compressedData);
      console.log(`${key} saved to localStorage (compressed):`, Array.isArray(data) ? data.length : 'object');
      return true;
    } catch (compressionError) {
      console.error(`Compression failed for ${key}:`, compressionError);
      try {
        localStorage.setItem(key, jsonData);
        console.log(`${key} saved to localStorage uncompressed:`, Array.isArray(data) ? data.length : 'object');
        return true;
      } catch (storageError) {
        console.error(`Failed to save ${key}:`, storageError);
        toast.error(`Failed to save ${key}. Your changes may not persist.`);
        return false;
      }
    }
  } catch (error) {
    console.error(`Error saving ${key} to localStorage:`, error);
    toast.error(`Failed to save ${key}. Your changes may not persist.`);
    return false;
  }
};

export const checkStorageVersion = (): boolean => {
  try {
    const currentVersion = localStorage.getItem('storageVersion');
    if (currentVersion !== STORAGE_VERSION) {
      console.log(`Storage version changed from ${currentVersion} to ${STORAGE_VERSION}. Using fresh data.`);
      localStorage.setItem('storageVersion', STORAGE_VERSION);
      return false; // Version changed, need to use fresh data
    }
    return true; // Version is current
  } catch (error) {
    console.error('Error checking localStorage cache:', error);
    return false;
  }
};
